import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { API_BASE_URL } from "../../api/header";
import { all_routes } from "../../Router/all_routes";
import {
  setbearer_token,
  setuser_data,
  setcurrent_store,
  setuserpermission_data,
  setcurrent_cart,
  setrolesandpermission_data,
} from "../redux/action";
import store from "../redux/store";

const TAB_LIST_KEY = "erf_active_tabs";
const TAB_ID_KEY = "erf_tab_id";
const SESSION_ENDED_KEY = "erf_session_ended";

// Generate a unique ID for this tab instance
const generateTabId = () =>
  `tab_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const clearAuthFromStorage = () => {
  try {
    const persistedRaw = localStorage.getItem("persist:root");
    if (persistedRaw) {
      const persisted = JSON.parse(persistedRaw);
      persisted.bearer_token = "null";
      persisted.user_data = "null";
      persisted.current_store = "null";
      persisted.userpermission_data = "[]";
      persisted.rolesandpermission_data = "null";
      persisted.current_cart = "[]";
      localStorage.setItem("persist:root", JSON.stringify(persisted));
    }
  } catch {
    localStorage.clear();
  }
};

const sendLogoutBeacon = () => {
  try {
    const state = store.getState();
    const token = state.bearer_token;
    if (!token) return;

    const url = `${API_BASE_URL}/logout`;
    const blob = new Blob(
      [JSON.stringify({ _token: token })],
      { type: "application/json" }
    );
    navigator.sendBeacon(url, blob);
  } catch {
    // Silently fail
  }
};

let hasRegisteredTab = false;

/**
 * Tracks open tabs via an array of unique tab IDs in localStorage.
 * Uses the PerformanceNavigationTiming API to distinguish between reloads and navigations.
 */
const useTabCloseLogout = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    // ── 1. Determine if this was a page reload ─────────────────────────────
    // The Performance API is the only reliable way to know if this was an F5 reload
    // vs a fresh navigation (or hitting Back after going to another site).
    let isReload = false;
    const navEntries = performance.getEntriesByType("navigation");
    if (navEntries.length > 0 && navEntries[0].type === "reload") {
      isReload = true;
    }

    // ── 2. Check if the session ended previously ───────────────────────────
    const sessionEnded = localStorage.getItem(SESSION_ENDED_KEY) === "true";

    if (sessionEnded) {
      if (isReload) {
        // False alarm: the user just hit refresh. 
        // We stay logged in and clear the ended flag.
        localStorage.removeItem(SESSION_ENDED_KEY);
      } else {
        // The session truly ended (all tabs closed, OR they went to another URL and came back).
        localStorage.removeItem(SESSION_ENDED_KEY);
        clearAuthFromStorage();
        sendLogoutBeacon();

        dispatch(setuser_data(null));
        dispatch(setbearer_token(null));
        dispatch(setcurrent_store(null));
        dispatch(setuserpermission_data([]));
        dispatch(setcurrent_cart([]));
        dispatch(setrolesandpermission_data([]));

        window.location.href = all_routes.signin;
        return;
      }
    }

    // ── 3. Manage unique Tab ID ────────────────────────────────────────────
    let tabId = sessionStorage.getItem(TAB_ID_KEY);
    if (!tabId) {
      tabId = generateTabId();
      sessionStorage.setItem(TAB_ID_KEY, tabId);
    }

    // ── 4. Add this tab to the active tabs array ───────────────────────────
    // Using a module-level flag prevents double-adding during React StrictMode/HMR
    if (!hasRegisteredTab) {
      hasRegisteredTab = true;
      const activeTabs = JSON.parse(localStorage.getItem(TAB_LIST_KEY) || "[]");
      if (!activeTabs.includes(tabId)) {
        activeTabs.push(tabId);
        localStorage.setItem(TAB_LIST_KEY, JSON.stringify(activeTabs));
      }
    }

    // ── 5. Handle Tab Close / Unload ───────────────────────────────────────
    const handleBeforeUnload = () => {
      const activeTabs = JSON.parse(localStorage.getItem(TAB_LIST_KEY) || "[]");
      const newTabs = activeTabs.filter((id) => id !== tabId);

      localStorage.setItem(TAB_LIST_KEY, JSON.stringify(newTabs));

      // If no tabs are left, mark the session as ended.
      if (newTabs.length === 0) {
        localStorage.setItem(SESSION_ENDED_KEY, "true");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // ── 6. Sync logout across active tabs ──────────────────────────────────
    const handleStorageChange = (event) => {
      if (event.key !== "persist:root") return;

      try {
        const newValue = event.newValue ? JSON.parse(event.newValue) : null;
        if (!newValue) return;

        const newToken = JSON.parse(newValue.bearer_token ?? "null");
        const currentToken = store.getState().bearer_token;

        if (currentToken && !newToken) {
          dispatch(setuser_data(null));
          dispatch(setbearer_token(null));
          dispatch(setcurrent_store(null));
          dispatch(setuserpermission_data([]));
          dispatch(setcurrent_cart([]));
          dispatch(setrolesandpermission_data([]));
          window.location.href = all_routes.signin;
        }
      } catch {
        // Ignore parse errors
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [dispatch]);
};

export default useTabCloseLogout;
